import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getComicBySlug, getRelatedComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { useState } from 'react';

export default function ComicDetail() {
  const { slug = '' } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug);
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到" description="抱歉，您请求的漫画不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您请求的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const currentCategory = categories.find((c) => c.slug === comic.category);
  const relatedComics = getRelatedComics(comic, 6);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 20);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '全部', href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 虫虫漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '虫虫漫画网',
      url: 'https://www.chongchongmanhua.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》可以在虫虫漫画网免费在线阅读，支持韩漫下拉式阅读模式，无需翻页即可流畅阅读。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}的更新频率是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: comic.status === '连载中'
            ? `《${comic.title}》目前为连载状态，持续更新中，最近更新于${comic.updatedAt}。`
            : `《${comic.title}》已完结，共${comic.chapters.length}话。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}`}
        description={`${comic.title} - ${comic.description.slice(0, 100)} 虫虫漫画网提供韩国漫画大全免费在线阅读，支持韩漫下拉式阅读。`}
        keywords={`${comic.title},韩国漫画,韩漫,免费漫画,${comic.tags.join(',')},${comic.author},虫虫漫画网`}
        canonicalUrl={`https://www.chongchongmanhua.com/comic/${comic.slug}`}
        ogImage={comic.cover}
        ogType="article"
        schema={{ ...comicSchema, ...faqSchema }}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="container-main">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    title={`${comic.title} - 韩国漫画大全免费在线阅读`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-user-line mr-1"></i> {comic.author}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500 mr-1"></i> {comic.rating}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex items-center gap-6 text-sm text-foreground-500">
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)} 万阅读
                  </span>
                  <span>
                    <i className="ri-heart-line mr-1"></i>
                    {(comic.favorites / 10000).toFixed(1)} 万收藏
                  </span>
                  <span>
                    <i className="ri-file-list-3-line mr-1"></i>
                    共 {comic.chapters.length} 话
                  </span>
                  <span>
                    <i className="ri-calendar-line mr-1"></i>
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                    <i className="ri-play-circle-line mr-1"></i> 开始阅读
                  </button>
                  <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1"></i> 收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-main">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-50 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap">
                  正序 <i className="ri-arrow-down-line ml-0.5"></i>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {displayedChapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-background-200 transition-colors group"
                >
                  <span className="text-sm text-foreground-700 truncate group-hover:text-primary-500 transition-colors">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                    {chapter.updatedAt}
                  </span>
                </Link>
              ))}
            </div>

            {comic.chapters.length > 20 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="px-6 py-2.5 rounded-full bg-background-50 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors"
                >
                  {showAllChapters ? '收起章节' : `查看全部 ${comic.chapters.length} 话`}
                  <i className={`ml-1 ${showAllChapters ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>
              </div>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="container-main">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((related) => (
                  <ComicCard key={related.id} comic={related} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}